import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";
import PageLayout from "app/components/common/PageLayout";
import CustomPagination from "app/components/common/Pagination";
import HowItWorksButton from "app/components/common/HowItWorksButton";
import CustomToast from "app/components/common/CustomToast";
import LoadingSpinner from "app/components/LoadingSpinner";
import UpgradePlanModal from "app/components/WebApp/GlobalNavBar/UpgradePlanModal";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import { useFlashToast } from "app/hooks/useFlashToast";
import AppointmentModal from "app/containers/JobCalendar/components/AppointmentModal";
import {
  fetchCalendarSettings,
  fetchSectionTemplates,
} from "app/containers/JobCalendar/slice";
import {
  fetchJobs,
  setActiveTab,
  setCurrentPage,
  JOBS_PER_PAGE,
} from "./slice";
import { isPaginatedTab } from "./utils/tabs";
import JobsTabs from "./components/JobsTabs";
import JobsTable from "./components/JobsTable";
import JobsMobileCards from "./components/JobsMobileCards";
import JobsEmptyState from "./components/JobsEmptyState";
import JobsHowItWorksModal from "./components/JobsHowItWorksModal";
import "./jobs.css";

const Jobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user: { profile },
    device: { isDesktop },
  } = useApp();

  const {
    activeTab,
    list,
    jobsCount,
    currentPage,
    counts,
    getJobsStatus,
    getJobsError,
  } = useSelector((state) => state.jobs);

  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { toast, hideToast } = useFlashToast();

  const isFreeUser = profile?.subscription?.tier === "Free";

  // Masked once here so the row components stay tier-unaware. Display only —
  // the raw values are still in the store, so entitlement is enforced server-side.
  const rows = useMemo(
    () =>
      isFreeUser
        ? list.map((job) => ({
            ...job,
            contract_status: null,
            payment_status: null,
            email_aggregate: null,
          }))
        : list,
    [list, isFreeUser]
  );
  // `idle` counts as loading: useInitialFetch dispatches in a passive effect, so
  // the first painted frame is still idle with an empty list. Without this the
  // empty state flashes for one frame on every visit to /jobs.
  const isInitialLoading =
    (getJobsStatus === "idle" || getJobsStatus === "loading") &&
    list.length === 0;

  // Initial load + the prerequisites the create modal needs. All mount-only, so
  // all StrictMode-guarded (CLAUDE.md §6) — a raw useEffect double-fires here.
  useInitialFetch(() => {
    // The store still holds the page we left on, so reset it first — otherwise
    // the old page's rows and pager render until the page-1 response lands.
    dispatch(setCurrentPage(1));
    dispatch(fetchJobs({ tab: activeTab, page: 1 }));
  });
  useInitialFetch(() => {
    dispatch(fetchCalendarSettings());
    dispatch(fetchSectionTemplates());
  });

  const handleTabChange = useCallback(
    (tab) => {
      if (tab === activeTab) return;
      dispatch(setActiveTab(tab));
      dispatch(fetchJobs({ tab, page: 1 }));
    },
    [dispatch, activeTab]
  );

  const handlePageChange = useCallback(
    (page) => {
      dispatch(setCurrentPage(page));
      dispatch(fetchJobs({ tab: activeTab, page }));
    },
    [dispatch, activeTab]
  );

  const handleRowClick = useCallback(
    (id) => navigate(`/jobs/${id}`),
    [navigate]
  );

  // TODO(AS-920): replace JobCalendar's AppointmentModal with the real Job
  // modals. It also closes when it hands over to Job Setup, so a job finished
  // through setup keeps its pre-setup chips until the next tab switch.
  const handleCloseCreate = useCallback(() => setShowCreateModal(false), []);

  const handleCreated = useCallback(() => {
    dispatch(fetchJobs({ tab: activeTab, page: currentPage }));
  }, [dispatch, activeTab, currentPage]);

  const headerActions = (
    <>
      <HowItWorksButton
        show="desktop"
        onClick={() => setShowHowItWorks(true)}
      />
      <Button variant="primary" onClick={() => setShowCreateModal(true)}>
        <Icon path={mdiPlus} size={0.85} className="me-1" />
        Job
      </Button>
    </>
  );

  const renderContent = () => {
    if (isInitialLoading) {
      return <LoadingSpinner type="partial" />;
    }

    if (getJobsStatus === "failed") {
      return (
        <div className="jobs-error-state">
          <p>{getJobsError || "Something went wrong loading jobs."}</p>
          <Button
            variant="outline-primary"
            onClick={() =>
              dispatch(fetchJobs({ tab: activeTab, page: currentPage }))
            }
          >
            Retry
          </Button>
        </div>
      );
    }

    if (list.length === 0) {
      return (
        <JobsEmptyState
          tab={activeTab}
          isFreeUser={isFreeUser}
          onCreate={() => setShowCreateModal(true)}
          onUpgrade={() => setShowUpgrade(true)}
        />
      );
    }

    return (
      <>
        {/* Table on desktop (>1024px), cards on tablet/mobile — matches the
            app's DeviceContext breakpoints (isDesktop). */}
        {isDesktop ? (
          <JobsTable jobs={rows} onRowClick={handleRowClick} />
        ) : (
          <JobsMobileCards jobs={rows} onRowClick={handleRowClick} />
        )}
        {isPaginatedTab(activeTab) && (
          <div className="jobs-pagination">
            <CustomPagination
              totalItems={jobsCount}
              itemsPerPage={JOBS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <PageLayout
      title="Jobs"
      titleIcon={
        <HowItWorksButton
          show="mobile"
          onClick={() => setShowHowItWorks(true)}
        />
      }
      headerActions={headerActions}
      banner={
        <JobsTabs
          activeTab={activeTab}
          counts={counts}
          onChange={handleTabChange}
        />
      }
      className="jobs-page"
    >
      {renderContent()}

      <UpgradePlanModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
      />

      <JobsHowItWorksModal
        show={showHowItWorks}
        onHide={() => setShowHowItWorks(false)}
      />

      <AppointmentModal
        show={showCreateModal}
        onHide={handleCloseCreate}
        onSaved={handleCreated}
        appointment={null}
        initialSlot={null}
      />

      <CustomToast
        show={toast.show}
        onClose={hideToast}
        message={toast.message}
        variant={toast.variant}
      />
    </PageLayout>
  );
};

export default Jobs;
